import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  goodsReturnsFromCustomer,
  type GRFCListItem,
} from '@/api/goodsReturns';
import { goodsSales } from '@/api/goodsSales';
import { contractors as contractorsApi } from '@/api/catalogs';
import Drawer from '@/components/Drawer';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DatePopover from '@/components/DatePopover';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 16);

export default function GoodsReturnsFromCustomerPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [selOrg, setSelOrg] = useState('');
  const [selSale, setSelSale] = useState('');
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(today());
  const [comment, setComment] = useState('');

  const list = useQuery({
    queryKey: ['grfc-list'],
    queryFn: () => goodsReturnsFromCustomer.list({ limit: 200 }),
  });
  // UX α: orgList більше не використовується — переключилися на
  // availableOrgs з usePermissionsByOrg (показуємо лише org з
  // sales.return.manage permission).
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('sales.return.manage');
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });
  const salesQ = useQuery({
    queryKey: ['goods-sales-posted'],
    queryFn: () => goodsSales.list({ posted: true, limit: 200 }),
    enabled: createOpen,
  });

  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const create = useMutation({
    mutationFn: () => goodsReturnsFromCustomer.createFromSale({
      organization_id: selOrg,
      document_number: number,
      document_date: new Date(date).toISOString(),
      base_sale_id: selSale,
      comment: comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['grfc-list'] });
      setCreateOpen(false);
      navigate(`/goods-returns-from-customer/${doc.id}`);
    },
  });

  const cols = useMemo<ColumnDef<GRFCListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: t('cd.date'), width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString(i18n.language) : '',
    },
    {
      key: 'contractor_id', header: 'Покупець',
      sortValue: (r) => cName.get(r.contractor_id) ?? '',
      cell: (r) => cName.get(r.contractor_id) ?? '',
    },
    {
      key: 'total_amount', header: t('cd.amount'), width: 140, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt(r.total_amount),
    },
    {
      key: 'posted', header: t('cd.posted'), width: 130, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => r.posted ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no'),
    },
  ], [t, i18n.language, cName]);

  // UX α: defaultну org беремо з availableOrgs (де є sales.return.manage)
  if (!selOrg && availableOrgs[0]) setSelOrg(availableOrgs[0].id);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Повернення від покупців</h2>
        <button
          className="primary"
          onClick={() => setCreateOpen(true)}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg
            ? `У вас немає прав створювати повернення у жодній з ${totalAccessibleOrgs} ваших організацій`
            : undefined
          }
        >
          + Створити з реалізації
        </button>
      </div>
      <DataTable<GRFCListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/goods-returns-from-customer/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />

      <Drawer
        open={createOpen}
        title="Нове повернення від покупця"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</button>
            <button
              className="primary"
              disabled={!selSale || !number || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common.create')}
            </button>
          </>
        }
      >
        <div className="field">
          <label>Організація</label>
          <select value={selOrg} onChange={(e) => setSelOrg(e.target.value)}>
            {availableOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>Документ-підстава (реалізація)</label>
          <select value={selSale} onChange={(e) => setSelSale(e.target.value)}>
            <option value="">—</option>
            {salesQ.data?.items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.document_number} ({new Date(s.document_date).toLocaleDateString('uk-UA')}) — {fmt(s.total_amount)}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>№ документа</label>
            <input value={number} onChange={(e) => setNumber(e.target.value)} required />
          </div>
          <div className="field">
            <label>Дата</label>
            <DatePopover withTime value={date} onChange={(v) => setDate(v)} />
          </div>
        </div>
        <div className="field">
          <label>Коментар</label>
          <input value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
      </Drawer>
    </>
  );
}
